import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import './ProfilePage.css';

import { API_BASE_URL, DEFAULT_PROFILE_PICTURE } from "../../Variables";
import BookDisplay from "../../components/BookDisplay";
import CurrentlyReadingDisplay from "../../components/CurrentlyReadingDisplay";
import BookDetailsModal from "../../components/BookDetailsModal";
import CurrentlyReadingDetailsModal from "../../components/CurrentlyReadingDetailsModal";
import ReviewsView from "../../components/ReviewsView";

function ProfilePage () {
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const { username } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState({});
  const [genrePreferences, setGenrePreferences] = useState("");
  const [error, setError] = useState(null);
  const [customShelves, setCustomShelves] = useState([]);
  const [currentlyReading, setCurrentlyReading] = useState(null);
  const [recentlyRead, setRecentlyRead] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [isPrivate, setIsPrivate] = useState(false);
  const [isCurrentUser, setIsCurrentUser] = useState(false);

  // Modals state
  const [displayBookDetailsModal, setDisplayBookDetailsModal] = useState(false);
  const [displayCurrentlyReadingModal, setDisplayCurrentlyReadingModal] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState(null);

  const handleListViewClick = (shelf) => {
    navigate(`/booknook/${shelf}?userId=${user.id}`);
  };

  // Handle book click - open book details modal
  const handleBookClick = (bookId) => {
    setSelectedBookId(bookId);
    setDisplayBookDetailsModal(true);
  };

  // Handle currently reading view - open currently reading modal
  const handleCurrentlyReadingView = (bookId) => {
    setSelectedBookId(bookId);
    setDisplayCurrentlyReadingModal(true);
  };

  const refreshShelves = () => {
    // no need to refresh shelves here
  };

  // Switch to currently reading view
  const switchToCurrentlyReadingView = () => {
    setDisplayBookDetailsModal(false);
    handleCurrentlyReadingView(selectedBookId);
  };

  // Switch to details view
  const switchToDetailsView = () => {
    setDisplayCurrentlyReadingModal(false);
    handleBookClick(selectedBookId);
  };


  // Fetch user data
  useEffect(() => {
    const controller = new AbortController();

    const fetchUser = async () => {
      try {
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/users/username/${username}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        });

        setUser(response.data);
        setIsCurrentUser(currentUser.id === response.data.id);
        if(response.data.profile_visibility === "private" && currentUser.id !== response.data.id) {
          setIsPrivate(true);
        }else{
          setIsPrivate(false);
        }
      } catch (err) {
        if (err.name !== "CanceledError") {
          setError("Failed to fetch user");
        }
      }
    };
  
    fetchUser();

    return () => controller.abort();
  }, [token, username, currentUser.id]);

  // Fetch profile picture
  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/profile-pictures/${user.profile_picture}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        });
    
        const imageUrl = URL.createObjectURL(response.data);
        setImagePreview(imageUrl);
      } catch (error) {
        setError("Failed to load profile picture");
      }
    };

    if(user.profile_picture){
      fetchProfilePicture();
    }
  }, [user, token]);

  // Fetch genre preferences
  useEffect(() => {
    const controller = new AbortController();

    const fetchGenrePreferences = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/genre-preferences/top`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { limit: 5, user_id: user.id },
          signal: controller.signal
        });

        let genrePreferences = "";
        for (let genre of response.data) {
          genrePreferences += genre.Genre.description + ", ";
        }
        genrePreferences = genrePreferences.slice(0, -2);
        setGenrePreferences(genrePreferences);

      } catch (error) {
        if (error.name !== 'CanceledError') {
          setError("Failed to load genre preferences. Please try again later.");
        }
      }
    };
    
    fetchGenrePreferences();

    return () => controller.abort();
  }, [token, user.id]);

  // Fetch custom shelves
  useEffect(() => {
    const controller = new AbortController();

    const fetchCustomShelves = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/shelves/custom`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { user_id: user.id },
          signal: controller.signal
        });
        setCustomShelves(response.data);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setError("Error fetching custom shelves:", error);
        }
      }
    };
    
    if(!isPrivate && user.id){
      fetchCustomShelves();
    }

    return () => controller.abort();
  }, [token, isPrivate, user.id]);

  // Fetch currently reading book
  useEffect(() => {
    const controller = new AbortController();

    const fetchCurrentlyReading = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/books/shelf-books`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { offset: 0, limit: 1, shelfName: "Currently Reading" , user_id: user.id },
          signal: controller.signal
        });
        setCurrentlyReading(response.data?.books[0]);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setError("Error fetching currently reading:", error);
        }
      }
    };
    
    if(!isPrivate && user.id){
      fetchCurrentlyReading();
    }

    return () => controller.abort();
  }, [token, isPrivate, user.id]);

  // Fetch recently read books
  useEffect(() => {
    const controller = new AbortController();

    const fetchRecentlyRead = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/books/shelf-books`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { offset: 0, limit: 5, shelfName: "Read" , user_id: user.id },
          signal: controller.signal
        });
        setRecentlyRead(response.data.books);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setError("Error fetching currently reading:", error);
        }
      }
    };
    
    if(!isPrivate && user.id){
      fetchRecentlyRead();
    }
    
    return () => controller.abort();
  }, [token, isPrivate, user.id]);

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-header-main">
          <div className="profile-header-content">
            <div className="signup-profile-picture" >
              <img src={user.profile_picture ? imagePreview : `${API_BASE_URL}${DEFAULT_PROFILE_PICTURE}`} alt="Profile" className="profile-picture"/>
            </div>
            {user.id === currentUser.id && <button className="secondary-button" onClick={() => navigate('/settings')}>Edit Profile</button>}
          </div>  
          
          <div className="profile-header-content profile-header-align profile-header-content-width">
            <h2>{user.username}</h2>
            <h4>{user.display_name}</h4>
            <p>{user.bio}</p>
          </div>
        </div>

        <div className="profile-header-content profile-header-align genre-preferences">
          {genrePreferences && <p><span className="bold">Genre Preferences:</span> {genrePreferences}</p>}
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {isPrivate ? (
        <div className="profile-content">
          <p className="private-message">This profile is private.</p>
        </div>
      ) : (    
        <div className="profile-content">

          {(user.mandatory_shelves_visibility === "public" || isCurrentUser ) && (
            <div className="profile-main-shelves">
              {/* Currently reading */}
              <div className="profile-shelf-view">
                <h2 className="content-title">Currently Reading</h2>
                {currentlyReading ? (
                  <CurrentlyReadingDisplay book={currentlyReading} onBookClick={handleBookClick}/>
                ):
                (
                  <p>No books currently reading.</p>
                )}

              </div>

              {/* Recently Read */}
              <div className="profile-shelf-view profile-shelf-preview">
                <h2 className="content-title">Recently Read</h2>
                {(recentlyRead && recentlyRead.length) > 0 ? (
                  <div className="list-preview shelf-preview ">
                    {recentlyRead.map((book, index) => (
                      <BookDisplay key={index} book={book} onBookClick={handleBookClick} />
                    ))}
                  </div>
                ): (
                  <p>No books read yet.</p>
                )}
              </div>
              
            </div>
          )}

          <div className="profile-main-shelves">
            {/* Custom shelves */}
            {(user.custom_shelves_visibility === "public" || isCurrentUser ) && (
              <div className="custom-shelves">
                <h2 className="content-title">Bookshelves</h2>
                <div className="custom-shelves-content">
                  {customShelves.length > 0 ? (
                    customShelves.map((shelf) => (
                      <div className="custom-shelf-item tertiary-button" key={shelf.id} onClick={() => handleListViewClick(shelf.name)}>
                        {shelf.name}
                      </div>
                    ))
                  ) : (
                    <p>No custom shelves created yet.</p>
                  )}
                </div>
              </div>
            )}

            
            {( (!isPrivate && user.reviews_visibility === "public") || isCurrentUser ) && (
              <div  className="custom-shelves">
                <h2 className="content-title">Reviews</h2>
                <ReviewsView userId={user.id} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {displayBookDetailsModal && (
        <BookDetailsModal
          setDisplayBookDetailsModal={setDisplayBookDetailsModal}
          bookId={selectedBookId}
          refreshShelves={refreshShelves}
          switchToCurrentlyReadingView={switchToCurrentlyReadingView}
        />
      )}

      {displayCurrentlyReadingModal && (
        <CurrentlyReadingDetailsModal
          setDisplayCurrentlyReadingModal={setDisplayCurrentlyReadingModal}
          bookId={selectedBookId}
          refreshShelves={refreshShelves}
          switchToDetailsView={switchToDetailsView}
        />
      )}
    </div>
  );
}

export default ProfilePage;
